import { useQuery } from '@tanstack/react-query';
import {
  type CompanyRole,
  type CompanyRolesResponse,
  type RoleNode,
  roleTree,
} from '@k-management/shared';
import { useMemo, useState } from 'react';
import { useCurrentUser } from '../../auth/AuthProvider.js';
import { Icon } from '../../components/Icon.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatTimestamp } from '../../lib/format.js';
import { queryKeys } from '../../lib/query-keys.js';
import { Highlighted, People, Person } from './organization-ui.js';
import { RoleDialog } from './RoleDialog.js';

interface Row {
  role: CompanyRole;
  depth: number;
  hasChildren: boolean;
}

/**
 * Organizzazione › Ruoli (03/10/2026): the organisation chart as a tree, who
 * holds each role and who stands in. Changed by who has «Modificare i ruoli
 * aziendali».
 */
export function RolesPage() {
  usePageTitle('Ruoli');
  const user = useCurrentUser();
  const canEdit = user.permissions.includes('organizzazione.roles');
  const query = useQuery({
    queryKey: queryKeys.companyRoles,
    queryFn: () => apiRequest<CompanyRolesResponse>('/organization/roles'),
  });
  const [search, setSearch] = useState('');
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<CompanyRole | 'new' | null>(null);

  const roles = useMemo(() => query.data?.roles ?? [], [query.data]);
  const rows = useMemo(() => visibleRows(roleTree(roles), closed, search), [roles, closed, search]);
  const parents = roles.filter((role) => roles.some((child) => child.parent === role.code));

  const toggle = (code: string) =>
    setClosed((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Ruoli</h1>
          <p className="muted">Chi fa cosa in K-Rev e chi lo sostituisce quando è assente.</p>
        </div>
        {canEdit && (
          <button type="button" className="primary" onClick={() => setEditing('new')}>
            Aggiungi ruolo
          </button>
        )}
      </div>

      {query.isPending && <p className="muted">Caricamento…</p>}
      {query.isError && <p className="error">{query.error.message}</p>}

      {query.data && (
        <div className="card stack">
          <div className="org-toolbar">
            <label className="search-box">
              <Icon name="search" size={16} />
              <input
                type="search"
                placeholder="Cerca un ruolo o una persona…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <button type="button" onClick={() => setClosed(new Set())}>
              Espandi tutto
            </button>
            <button
              type="button"
              onClick={() => setClosed(new Set(parents.map((role) => role.code)))}
            >
              Comprimi tutto
            </button>
            <span className="org-spacer" />
            <span className="muted small org-legend">
              <span className="org-kind int">INT</span> interno
              <span className="org-kind est">EST</span> esterno
            </span>
          </div>
          <div className="table-wrap">
            <table className="data-table org-roles">
              <thead>
                <tr>
                  <th>Ruolo</th>
                  <th>Assegnato a</th>
                  <th>Sostituto</th>
                  <th>Note</th>
                  {canEdit && <th aria-label="Modifica" />}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ role, depth, hasChildren }) => {
                  const open = search.trim() !== '' || !closed.has(role.code);
                  return (
                    <tr key={role.code}>
                      <td
                        className="org-role-name"
                        style={{ paddingLeft: `${0.75 + depth * 1.4}rem` }}
                      >
                        {hasChildren ? (
                          <button
                            type="button"
                            className="org-twisty"
                            aria-expanded={open}
                            aria-label={open ? `Comprimi ${role.title}` : `Espandi ${role.title}`}
                            onClick={() => toggle(role.code)}
                          >
                            {open ? '▾' : '▸'}
                          </button>
                        ) : (
                          <span className="org-twisty leaf">·</span>
                        )}
                        <strong>
                          <Highlighted text={role.title} query={search} />
                        </strong>
                      </td>
                      <td>
                        <People people={role.holders} highlight={search} />
                      </td>
                      <td>
                        {role.substitute ? (
                          <Person person={role.substitute} highlight={search} />
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="org-notes">{role.notes}</td>
                      {canEdit && (
                        <td>
                          <button
                            type="button"
                            className="icon-button"
                            aria-label={`Modifica ${role.title}`}
                            title="Modifica"
                            onClick={() => setEditing(role)}
                          >
                            <Icon name="edit" size={16} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={canEdit ? 5 : 4} className="muted">
                      Nessun ruolo trovato.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card stack">
        <h2>Su ogni offerta e commessa</h2>
        <dl className="org-deal-roles">
          <dt>Commerciale</dt>
          <dd>
            Uno per offerta: chi segue l’opportunità e prepara l’offerta, qualunque sia la sua area.
          </dd>
          <dt>PM</dt>
          <dd>
            Uno per commessa, nominato dal Responsabile Operations alla firma del contratto; deve
            essere un utente di K-Management.
          </dd>
        </dl>
      </div>

      <p className="muted small">
        I ruoli aziendali non sono i profili di accesso al programma.
        {query.data?.updatedAt && ` Ultima modifica: ${formatTimestamp(query.data.updatedAt)}.`}
      </p>

      {editing && (
        <RoleDialog
          role={editing === 'new' ? null : editing}
          roles={roles}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

/**
 * The rows of the tree to show: the children of a closed role are hidden;
 * while searching, every role matching and the roles above it.
 */
function visibleRows(tree: RoleNode[], closed: ReadonlySet<string>, search: string): Row[] {
  const needle = search.trim().toLowerCase();
  const matches = (role: CompanyRole) =>
    [role.title, ...role.holders.map((person) => person.name), role.substitute?.name ?? '']
      .join(' ')
      .toLowerCase()
      .includes(needle);
  const keep = (node: RoleNode): boolean => matches(node.role) || node.children.some(keep);
  const rows: Row[] = [];
  const walk = (nodes: RoleNode[], depth: number) => {
    for (const node of nodes) {
      if (needle && !keep(node)) continue;
      rows.push({ role: node.role, depth, hasChildren: node.children.length > 0 });
      if (needle || !closed.has(node.role.code)) walk(node.children, depth + 1);
    }
  };
  walk(tree, 0);
  return rows;
}
